import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

type ToolCall = {
  id: string;
  tool: string;
  parameters: Record<string, unknown>;
  status: "pending" | "success" | "error";
  startedAt: string;
  completedAt?: string;
  resultSnippet?: string;
  error?: string;
};

type HaikuTestPayload = {
  answer: string;
  toolCalls: ToolCall[];
  usage?: {
    inputTokens?: number | null;
    outputTokens?: number | null;
    totalTokens?: number | null;
  };
  error?: string;
};

const MODEL_OPTIONS = [
  { label: "Claude Haiku 4.5", value: "claude-haiku-4-5" },
  { label: "Claude Sonnet 4.5", value: "claude-sonnet-4-5" },
];

const samplePrompts = [
  "what is our commission with blacklane?",
  "who is our princess rep?",
];

export default function HaikuTest() {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState<"claude-haiku-4-5" | "claude-sonnet-4-5">(
    "claude-haiku-4-5",
  );
  const [topK, setTopK] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HaikuTestPayload | null>(null);

  const runPrompt = async () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch("/api/haiku-test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, model, topK }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Request failed");
      }

      const data = (await response.json()) as HaikuTestPayload;
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-10">
      <div>
        <h1 className="text-3xl font-semibold">Haiku Tool Sandbox</h1>
        <p className="mt-2 text-muted-foreground">
          Send prompts directly to Claude Haiku 4.5 with Pinecone and CRM tools.
          Inspect which MCP tools were invoked for each question.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Prompt</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex min-w-[200px] flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground">
                Model
              </label>
              <select
                className="rounded-md border px-3 py-2 text-sm"
                value={model}
                onChange={(event) =>
                  setModel(event.target.value as "claude-haiku-4-5" | "claude-sonnet-4-5")
                }
                disabled={loading}
              >
                {MODEL_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex min-w-[200px] flex-col gap-1">
              <label className="text-sm font-medium text-muted-foreground">
                Pinecone top_k
              </label>
              <select
                className="rounded-md border px-3 py-2 text-sm"
                value={topK}
                onChange={(event) => setTopK(Number(event.target.value))}
                disabled={loading}
              >
                {Array.from({ length: 20 }, (_, index) => index + 1).map(
                  (value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ),
                )}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {samplePrompts.map((example) => (
              <Button
                key={example}
                variant="secondary"
                size="sm"
                disabled={loading}
                onClick={() => setPrompt(example)}
              >
                {example}
              </Button>
            ))}
          </div>
          <Textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            rows={5}
            placeholder="Ask about commissions, supplier reps, marketing copy, or business workflows…"
          />
          <Button onClick={runPrompt} disabled={loading} className="w-fit">
            {loading ? "Sending…" : "Send to Haiku"}
          </Button>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      {result && (
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Answer</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-line text-lg">{result.answer}</p>
              {result.usage && (
                <div className="mt-4 flex flex-wrap gap-3 text-sm text-muted-foreground">
                  <span>
                    Input tokens: {result.usage.inputTokens ?? "—"}
                  </span>
                  <span>
                    Output tokens: {result.usage.outputTokens ?? "—"}
                  </span>
                  <span>
                    Total tokens: {result.usage.totalTokens ?? "—"}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tool Calls</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {result.toolCalls.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No tool calls were recorded for this prompt.
                </p>
              )}
              {result.toolCalls.map((call) => (
                <div
                  key={call.id}
                  className="rounded-lg border p-4 shadow-sm transition hover:border-primary/50"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{call.tool}</span>
                    <Badge
                      variant={
                        call.status === "success"
                          ? "default"
                          : call.status === "error"
                            ? "destructive"
                            : "secondary"
                      }
                    >
                      {call.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(call.startedAt).toLocaleTimeString()}
                      {call.completedAt
                        ? ` → ${new Date(call.completedAt).toLocaleTimeString()}`
                        : ""}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    <span className="font-medium">Parameters:</span>{" "}
                    <code className="break-all">
                      {JSON.stringify(call.parameters)}
                    </code>
                  </div>
                  {call.resultSnippet && (
                    <div className="mt-2 text-sm">
                      <span className="font-medium">Result:</span>{" "}
                      <span className="whitespace-pre-wrap">
                        {call.resultSnippet}
                      </span>
                    </div>
                  )}
                  {call.error && (
                    <p className="mt-2 text-sm text-destructive">
                      Error: {call.error}
                    </p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

